import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { formatDate } from '@angular/common';
import { BackendApiService } from './backend-api.service';

export interface UserPreferences {
  timeZone: string;
  dateFormat: string;
  defaultLandingPage: 'roadmap' | 'backlog' | 'sprint' | 'team' | 'profile';
  defaultRoadmapZoom: 'week' | 'month' | 'quarter';
  weekStartsOn: 'monday' | 'sunday';
  emailNotifications?: boolean;
  assignmentNotifications?: boolean;
  mentionNotifications?: boolean;
  dailyDigest?: boolean;
}

export const DEFAULT_USER_PREFERENCES: UserPreferences = {
  timeZone: 'Australia/Melbourne',
  dateFormat: 'dd/MM/yyyy',
  defaultLandingPage: 'roadmap',
  defaultRoadmapZoom: 'month',
  weekStartsOn: 'monday',
  emailNotifications: false,
  assignmentNotifications: false,
  mentionNotifications: false,
  dailyDigest: false
};

@Injectable({
  providedIn: 'root'
})
export class UserPreferencesService {
  private readonly storageKey = 'sprinta.profile.preferences';
  private preferencesSubject = new BehaviorSubject<UserPreferences>(this.loadInitialPreferences());
  public preferences$: Observable<UserPreferences> = this.preferencesSubject.asObservable();

  constructor(private backendApi: BackendApiService) {
    // Attempt to hydrate preferences from backend user profile if available
    this.backendApi.getCurrentUser().pipe(
      catchError(() => of(null))
    ).subscribe(res => {
      if (res?.user?.preferences) {
        const backendPrefs = res.user.preferences as Partial<UserPreferences>;
        const current = this.preferencesSubject.value;
        const merged: UserPreferences = {
          ...current,
          ...backendPrefs
        };
        this.persistLocal(merged);
        this.preferencesSubject.next(merged);
      }
    });
  }

  public getPreferences(): UserPreferences {
    return this.preferencesSubject.value;
  }

  public savePreferences(updates: Partial<UserPreferences>): void {
    const current = this.preferencesSubject.value;
    const updated: UserPreferences = {
      ...current,
      ...updates
    };

    this.persistLocal(updated);
    this.preferencesSubject.next(updated);

    // Sync to AWS API backend
    this.backendApi.updateProfile({ preferences: updated }).pipe(
      catchError(err => {
        console.warn('UserPreferencesService: profile sync warning', err);
        return of(null);
      })
    ).subscribe();
  }

  public resetPreferences(): void {
    this.savePreferences(DEFAULT_USER_PREFERENCES);
  }

  public getLandingPageRoute(projectId?: string): string {
    const landing = this.preferencesSubject.value.defaultLandingPage;
    let path = 't/roadmap';
    switch (landing) {
      case 'backlog':
        path = 't/backlog';
        break;
      case 'sprint':
        path = 't/sprint';
        break;
      case 'team':
        path = 'u/team';
        break;
      case 'profile':
        path = 'u/profile';
        break;
      case 'roadmap':
      default:
        path = 't/roadmap';
        break;
    }
    return projectId ? `/p/${projectId}/${path}` : `/${path}`;
  }

  public getRoadmapScale(): 'week' | 'month' | 'quarter' {
    return this.preferencesSubject.value.defaultRoadmapZoom || 'month';
  }

  public getWeekStartsOn(): 'monday' | 'sunday' {
    return this.preferencesSubject.value.weekStartsOn || 'monday';
  }

  public formatDate(date: Date | string | number | null | undefined, customFormat?: string): string {
    if (!date) return '';
    const d = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
    if (!d || isNaN(d.getTime())) return '';

    const prefs = this.preferencesSubject.value;
    const targetFormat = customFormat || prefs.dateFormat || 'dd/MM/yyyy';
    const timeZone = prefs.timeZone || 'Australia/Melbourne';

    try {
      return formatDate(d, targetFormat, 'en-AU', timeZone);
    } catch {
      try {
        return d.toLocaleDateString('en-AU', { timeZone });
      } catch {
        return d.toLocaleDateString('en-AU');
      }
    }
  }

  private loadInitialPreferences(): UserPreferences {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        return {
          ...DEFAULT_USER_PREFERENCES,
          ...parsed
        };
      }
    } catch (e) {
      console.warn('UserPreferencesService: failed to parse stored preferences', e);
    }
    return { ...DEFAULT_USER_PREFERENCES };
  }

  private persistLocal(prefs: UserPreferences): void {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(prefs));
    } catch (e) {
      console.warn('UserPreferencesService: failed to write preferences to localStorage', e);
    }
  }
}
